/**
 * iter150AZ.13 — Admin triage UI for the AI feedback queue.
 *
 * Lists every pending thumbs-down on assistant replies and lets an
 * admin one-click *Promote* (writes the correction into ai_training so
 * every future chat sees it as a canonical fact) or *Dismiss* (drops
 * spurious complaints). Optionally lets the admin polish the
 * correction text before promoting.
 *
 * Wired by iter150AZ.12 endpoints:
 *   GET  /api/ai/feedback/pending
 *   POST /api/ai/feedback/{id}/promote
 *   POST /api/ai/feedback/{id}/dismiss
 */
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, ThumbsUp, ThumbsDown, Spinner, ArrowClockwise, CheckCircle,
  X as XIcon, ChatCircleDots, GraduationCap,
} from "@phosphor-icons/react";
import {
  listAiPendingFeedback, promoteAiFeedback, dismissAiFeedback,
} from "@/lib/api";
import { useT } from "@/lib/i18n";

function FeedbackCard({ row, userEmail, onChanged }) {
  const t = useT();
  const [editing, setEditing] = useState(false);
  const [factOverride, setFactOverride] = useState(row.correction || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [dismissOpen, setDismissOpen] = useState(false);
  const [dismissReason, setDismissReason] = useState("");

  const promote = useCallback(async () => {
    setBusy(true);
    setError("");
    try {
      const fact = (factOverride || "").trim();
      await promoteAiFeedback(row.id, {
        requester_email: userEmail,
        fact: fact || null,
      });
      onChanged?.(row.id, "promoted");
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Promote failed");
    } finally {
      setBusy(false);
    }
  }, [row.id, factOverride, userEmail, onChanged]);

  const dismiss = useCallback(async () => {
    setBusy(true);
    setError("");
    try {
      await dismissAiFeedback(row.id, {
        requester_email: userEmail,
        reason: (dismissReason || "").trim() || null,
      });
      onChanged?.(row.id, "dismissed");
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Dismiss failed");
    } finally {
      setBusy(false);
    }
  }, [row.id, dismissReason, userEmail, onChanged]);

  const correction = (row.correction || "").trim();
  const hasCorrection = correction.length > 0;
  const canPromote = ((factOverride || "").trim().length > 0) && !busy;

  return (
    <div
      data-testid={`ai-feedback-row-${row.id}`}
      className="bg-white border border-slate-200 rounded-sm p-4 space-y-3"
    >
      {/* Header: who + when */}
      <div className="flex items-center justify-between text-[11px] text-slate-500">
        <span className="inline-flex items-center gap-1">
          <ThumbsDown size={11} weight="fill" className="text-rose-500" />
          <span className="font-semibold text-rose-700">{row.rated_by}</span>
          <span className="text-slate-400">·</span>
          <span>{row.rated_at?.slice(0, 19)?.replace("T", " ")}</span>
        </span>
        <span className="text-slate-400">session {String(row.session_id || "").slice(0, 8)}</span>
      </div>

      {/* Original assistant reply */}
      <div>
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">
          {t("ai_feedback.assistant_said", "Assistant said")}
        </div>
        <div
          data-testid={`ai-feedback-original-${row.id}`}
          className="text-xs bg-slate-50 border border-slate-200 rounded-sm p-2.5 text-slate-700 whitespace-pre-wrap max-h-32 overflow-auto"
        >
          {row.assistant_content || "(empty)"}
        </div>
      </div>

      {/* User's correction (read-only display + editable when promoting) */}
      <div>
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">
          {t("ai_feedback.user_correction", "User's correction")}
          {!hasCorrection && (
            <span className="ml-1 italic text-amber-600 normal-case font-normal">
              — {t("ai_feedback.no_correction", "no text provided, you must type one before promoting")}
            </span>
          )}
        </div>
        {editing ? (
          <textarea
            value={factOverride}
            onChange={(e) => setFactOverride(e.target.value)}
            data-testid={`ai-feedback-fact-${row.id}`}
            placeholder={t("ai_feedback.fact_placeholder",
              "Write the canonical fact that should land in ai_training (≤2000 chars)")}
            rows={3}
            maxLength={2000}
            className="w-full text-xs px-2 py-1.5 rounded-sm border border-emerald-300 focus:outline-none focus:ring-2 focus:ring-emerald-200 resize-y"
          />
        ) : (
          <div className="text-xs bg-rose-50 border border-rose-100 rounded-sm p-2.5 text-slate-800 whitespace-pre-wrap">
            {hasCorrection ? correction : <em className="text-slate-400">(none)</em>}
          </div>
        )}
      </div>

      {/* Error banner */}
      {error && (
        <div data-testid={`ai-feedback-error-${row.id}`}
             className="text-xs bg-red-50 border border-red-200 rounded-sm p-2 text-red-700">
          {error}
        </div>
      )}

      {/* Action row */}
      <div className="flex flex-wrap items-center gap-2 pt-1">
        {!editing && (
          <button
            onClick={() => setEditing(true)}
            data-testid={`ai-feedback-edit-${row.id}`}
            disabled={busy}
            className="h-8 px-2.5 text-[11px] font-semibold rounded-sm bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 disabled:opacity-50 inline-flex items-center gap-1"
          >
            <GraduationCap size={11} weight="bold" />
            {hasCorrection
              ? t("ai_feedback.edit_fact", "Edit before promoting")
              : t("ai_feedback.write_fact", "Write canonical fact")}
          </button>
        )}
        <button
          onClick={promote}
          disabled={!canPromote}
          data-testid={`ai-feedback-promote-${row.id}`}
          className="h-8 px-3 text-[11px] font-bold rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-40 inline-flex items-center gap-1.5"
        >
          {busy ? <Spinner size={10} className="animate-spin" /> : <CheckCircle size={11} weight="bold" />}
          {t("ai_feedback.promote", "Promote to training")}
        </button>
        <button
          onClick={() => setDismissOpen(v => !v)}
          disabled={busy}
          data-testid={`ai-feedback-dismiss-toggle-${row.id}`}
          className="h-8 px-2.5 text-[11px] font-semibold rounded-sm bg-white border border-slate-300 hover:bg-slate-50 text-slate-600 disabled:opacity-50 inline-flex items-center gap-1"
        >
          <XIcon size={11} weight="bold" />
          {t("ai_feedback.dismiss", "Dismiss")}
        </button>
      </div>

      {dismissOpen && (
        <div className="pt-2 border-t border-slate-100 space-y-1.5" data-testid={`ai-feedback-dismiss-form-${row.id}`}>
          <input
            type="text"
            value={dismissReason}
            onChange={(e) => setDismissReason(e.target.value)}
            placeholder={t("ai_feedback.dismiss_reason_placeholder",
              "Why is this not actionable? (optional, ≤500 chars)")}
            maxLength={500}
            data-testid={`ai-feedback-dismiss-reason-${row.id}`}
            className="w-full text-xs px-2 py-1.5 rounded-sm border border-slate-300 focus:outline-none focus:ring-1 focus:ring-slate-400"
          />
          <div className="flex gap-1.5">
            <button
              onClick={dismiss}
              disabled={busy}
              data-testid={`ai-feedback-dismiss-confirm-${row.id}`}
              className="h-7 px-2 text-[11px] font-bold rounded-sm bg-slate-700 hover:bg-slate-800 text-white disabled:opacity-50"
            >
              {busy ? <Spinner size={10} className="animate-spin" /> : t("ai_feedback.dismiss_confirm", "Confirm dismiss")}
            </button>
            <button
              onClick={() => { setDismissOpen(false); setDismissReason(""); }}
              disabled={busy}
              className="h-7 px-2 text-[11px] font-semibold rounded-sm text-slate-500 hover:bg-slate-100"
            >
              {t("action.cancel", "Cancel")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AiFeedbackPage({ user, onLogout }) {
  const t = useT();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const r = await listAiPendingFeedback(user?.email);
      setItems(r.items || []);
      setCount(r.count || 0);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Could not load feedback");
    } finally {
      setLoading(false);
    }
  }, [user?.email]);

  useEffect(() => {
    if (user?.email) load();
  }, [user?.email, load]);

  const onChanged = useCallback((id, verdict) => {
    setItems(prev => prev.filter(r => r.id !== id));
    setCount(c => Math.max(0, c - 1));
    setToast(verdict === "promoted"
      ? t("ai_feedback.promoted_toast", "Promoted to training — every future chat now knows this.")
      : t("ai_feedback.dismissed_toast", "Dismissed."));
    setTimeout(() => setToast(""), 3500);
  }, [t]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="ai-feedback-page">
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => navigate("/")}
            data-testid="ai-feedback-back"
            className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900"
          >
            <ArrowLeft size={14} /> {t("nav.back", "Back")}
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={load}
              disabled={loading}
              data-testid="ai-feedback-refresh"
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 h-8 rounded-sm border border-slate-300 hover:bg-slate-50 disabled:opacity-50"
            >
              {loading ? <Spinner size={11} className="animate-spin" /> : <ArrowClockwise size={11} weight="bold" />}
              {t("action.refresh", "Refresh")}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <ChatCircleDots size={22} weight="bold" className="text-slate-500" />
              {t("ai_feedback.title", "AI feedback queue")}
            </h1>
            <p className="text-xs text-slate-500 mt-1 max-w-2xl">
              {t("ai_feedback.subtitle",
                "Every thumbs-down on an assistant reply lands here. Promote good corrections into ai_training so every future chat picks them up automatically.")}
            </p>
          </div>
          <div data-testid="ai-feedback-count"
               className="text-xs font-bold uppercase tracking-wider text-slate-500 bg-white border border-slate-200 rounded-sm px-2.5 py-1.5 inline-flex items-center gap-1.5">
            <ThumbsDown size={11} weight="bold" className="text-rose-500" />
            {count} {t("ai_feedback.pending_label", "pending")}
          </div>
        </div>

        {toast && (
          <div data-testid="ai-feedback-toast"
               className="bg-emerald-50 border border-emerald-200 rounded-sm px-3 py-2 text-sm text-emerald-800 inline-flex items-center gap-2">
            <ThumbsUp size={13} weight="bold" /> {toast}
          </div>
        )}
        {error && (
          <div data-testid="ai-feedback-error"
               className="bg-red-50 border border-red-200 rounded-sm px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        {loading && items.length === 0 && (
          <div className="text-center text-slate-400 py-12">
            <Spinner size={20} className="animate-spin inline" />
          </div>
        )}
        {!loading && items.length === 0 && (
          <div data-testid="ai-feedback-empty"
               className="text-center py-12 text-slate-400 text-sm">
            <ThumbsUp size={28} weight="duotone" className="inline mb-2 text-emerald-300" />
            <div>{t("ai_feedback.empty", "No pending feedback. The bot is doing fine.")}</div>
          </div>
        )}

        <div className="space-y-3">
          {items.map(row => (
            <FeedbackCard
              key={row.id}
              row={row}
              userEmail={user?.email}
              onChanged={onChanged}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
